import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { downloadPdf } from "@/lib/api";
import { BlocSwitcher } from "@/components/shared";
import { Users, Loader2, Download, CheckCircle2, AlertTriangle, Clock, Minus } from "lucide-react";
import { btnGhost } from "./adminShared";

const PROG_STATUS = {
  validated: { label: "Validée", cls: "bg-emerald-100 text-emerald-600", Icon: CheckCircle2 },
  to_revise: { label: "À revoir", cls: "bg-amber-100 text-amber-600", Icon: AlertTriangle },
  done: { label: "Déposée (à évaluer)", cls: "bg-sky-100 text-sky-600", Icon: Clock },
  none: { label: "Rien déposé", cls: "bg-slate-50 text-slate-300", Icon: Minus },
};

export default function ClassProgressTab() {
  const [sequences, setSequences] = useState([]);
  const [seqId, setSeqId] = useState(null);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportProgressPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf(`/admin/class-progress/pdf?sequence_id=${seqId}`, "progression_classe.pdf");
      toast.success("Progression exportée ✓");
    } catch {
      toast.error("Échec de l'export PDF");
    } finally { setExporting(false); }
  };

  useEffect(() => {
    api.get("/sequences").then(({ data }) => { setSequences(data); setSeqId((p) => p || data[0]?.id); });
  }, []);

  useEffect(() => {
    if (!seqId) return;
    setData(null);
    api.get(`/admin/class-progress?sequence_id=${seqId}`).then(({ data }) => setData(data));
  }, [seqId]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-slate-500 font-medium">Qui a déposé / validé quoi, séance par séance.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportProgressPdf} disabled={exporting || !seqId}
            className={btnGhost + " px-4 py-2.5 rounded-full border border-slate-200 disabled:opacity-50"} data-testid="export-progress-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exporter (PDF)
          </button>
          <BlocSwitcher sequences={sequences} value={seqId} onChange={setSeqId} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {Object.entries(PROG_STATUS).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
            <span className={`inline-flex items-center justify-center h-6 w-6 rounded-lg ${v.cls}`}><v.Icon className="h-3.5 w-3.5" /></span>
            {v.label}
          </span>
        ))}
      </div>
      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 text-orange-400 animate-spin" /></div>
      ) : data.students.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-12 text-center">
          <Users className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="text-slate-500 font-medium mt-3">Aucun élève inscrit.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-slate-100 bg-white shadow-sm" data-testid="class-progress-table">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left text-[11px] font-extrabold uppercase tracking-wide text-slate-400 px-4 py-3 sticky left-0 bg-slate-50 z-10">Élève</th>
                {data.seances.map((s) => (
                  <th key={s.id} className="px-3 py-3 text-center" title={s.title}>
                    <span className="text-[11px] font-extrabold text-slate-500 block">S{s.order}</span>
                    <span className="text-[10px] font-bold text-orange-500">{s.competency_code}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.students.map((st) => (
                <tr key={st.id} className="border-t border-slate-100" data-testid={`progress-row-${st.id}`}>
                  <td className="px-4 py-3 sticky left-0 bg-white z-10">
                    <p className="font-bold text-slate-800 text-sm whitespace-nowrap">{st.name}</p>
                  </td>
                  {data.seances.map((s) => {
                    const v = PROG_STATUS[st.cells[s.id] || "none"];
                    return (
                      <td key={s.id} className="px-3 py-3 text-center">
                        <span title={v.label} className={`inline-flex items-center justify-center h-8 w-8 rounded-xl ${v.cls}`} data-testid={`progress-cell-${st.id}-${s.id}`}>
                          <v.Icon className="h-4 w-4" />
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
